import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

// Create base closet API
const closetApi = createBaseApi('closetApi');

// Enhanced API with closet endpoints
export const enhancedClosetApi = closetApi.injectEndpoints({
  endpoints: (builder) => ({
    closetPage: builder.query({
      query: (params) => ({ url: '/closets/me', params }),
      providesTags: ['Closets'],
      transformResponse: response => ({ items: response.data || response.results || response.items || [], pagination: response.pagination })
    }),

    // Get all user's clothing items
    getUserClothing: builder.query({
      query: () => ({
        url: API_ENDPOINTS.CLOSET.USER_DATA,
        method: 'GET'
      }),
      // Auto-refetch when tab regains focus (e.g., after saving from browser extension)
      refetchOnFocus: true,
      providesTags: (result) =>
        result?.success && result?.data?.items
          ? [
            'ClosetClothes',
            ...result.data.items.map(({ id }) => ({ type: 'ClosetClothes', id }))
          ]
          : ['ClosetClothes'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.items) {
          // Transform backend data to frontend format
          return response.data.items.map(item => ({
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            useRemovedImage: item.useRemovedImage || false,
            isPublic: item.isSharedForTryOn ?? item.isPublic ?? false,
            source_listing: item.source_listing,
            source_product: item.source_product,
            // Provenance — set when the item was materialised from a shared
            // try-on result, so it can be re-shared as a look, not a garment
            sourceVtoResult: item.sourceVtoResult?.id || item.sourceVtoResult || null,
            origin: item.origin || 'upload',
            licensingEnabled: item.source_listing?.licensingEnabled || item.source_product?.licensingEnabled || item.licensingEnabled || false,
            licensingType: item.source_listing?.licensingType || item.source_product?.licensingType || item.licensingType || null,
            licensingPrice: item.source_listing?.licensingPrice || item.source_product?.licensingPrice || item.licensingPrice || null,
            currency: item.source_listing?.currency || item.source_product?.currency || item.currency || 'USD',
            generatedProductPhoto: item.source_listing?.generatedProductPhoto ? {
              id: item.source_listing.generatedProductPhoto.id,
              url: item.source_listing.generatedProductPhoto.url
            } : item.source_product?.generatedProductPhoto ? {
              id: item.source_product.generatedProductPhoto.id,
              url: item.source_product.generatedProductPhoto.url
            } : null,
            addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          }));
        }
        return [];
      }
    }),

    // Upload clothing item with file
    uploadClothing: builder.mutation({
      query: (args) => {
        const { file, clothesName, size, isPublic } = args;
        const formData = new FormData();
        formData.append('image', file);
        formData.append('clothesName', clothesName);
        if (size) {
          formData.append('size', size);
        }
        if (isPublic !== undefined) {
          formData.append('isSharedForTryOn', isPublic);
        }

        return {
          url: API_ENDPOINTS.CLOSET.CREATE_WITH_UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['ClosetClothes'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Upload failed');
      }
    }),

    // Create clothing item with existing image ID
    createClothing: builder.mutation({
      query: ({ clothesName, size, imageId }) => ({
        url: API_ENDPOINTS.CLOSET.CREATE,
        method: 'POST',
        body: {
          clothesName,
          size,
          imageId
        }
      }),
      invalidatesTags: ['ClosetClothes'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Creation failed');
      }
    }),

    // Create clothing item from image URL (used by email import)
    createClothingFromUrl: builder.mutation({
      query: ({ imageUrl, clothesName, closetId }) => ({
        url: '/closet-clothes/from-url',
        method: 'POST',
        body: { imageUrl, clothesName, closetId }
      }),
      invalidatesTags: ['ClosetClothes'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            closetId: response.closetId,
            closetName: response.closetName
          };
        }
        throw new Error(response?.message || 'Failed to create from URL');
      }
    }),

    // Add a SHEYOU product to the closet so it can be worn.
    // Designer pieces live in `products`, not in anyone's closet, so a try-on
    // needs a closet row first. The backend stamps the DESIGNER as
    // originalUploader, which is what routes the try-on earning to them.
    addFromListing: builder.mutation({
      query: ({ listingId, closetId, clothesName }) => ({
        url: '/closet-clothes/from-listing',
        method: 'POST',
        body: { listingId, closetId, clothesName }
      }),
      invalidatesTags: ['ClosetClothes'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null
          };
        }
        throw new Error(response?.message || 'Failed to add item to closet');
      }
    }),

    // Extract clothing images from a webpage URL
    extractClothingFromUrl: builder.mutation({
      query: (url) => ({
        url: '/closet-clothes/extract-from-url',
        method: 'POST',
        body: { url }
      }),
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        throw new Error(response?.message || 'Failed to extract images');
      }
    }),

    // Update clothing item
    updateClothing: builder.mutation({
      query: ({ id, isPublic, ...updates }) => ({
        url: API_ENDPOINTS.CLOSET.UPDATE(id),
        method: 'PUT',
        body: { ...updates, isSharedForTryOn: isPublic }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes',
        'SharedCloset'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            isPublic: item.isSharedForTryOn ?? item.isPublic ?? false,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Update failed');
      }
    }),

    // Update clothing item with file upload (for cropped images)
    updateClothingWithUpload: builder.mutation({
      query: ({ id, file, clothesName, size, isPublic }) => {
        const formData = new FormData();
        if (file) formData.append('image', file);
        if (clothesName !== undefined) formData.append('clothesName', clothesName);
        if (size !== undefined) formData.append('size', size);
        if (isPublic !== undefined) formData.append('isSharedForTryOn', isPublic);

        return {
          url: `${API_ENDPOINTS.CLOSET.UPDATE(id)}/upload`,
          method: 'PUT',
          body: formData
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes',
        'SharedCloset'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url,
              thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            useRemovedImage: item.useRemovedImage || false,
            addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Update with upload failed');
      }
    }),

    // Delete clothing item
    deleteClothing: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.CLOSET.DELETE(id),
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes'
      ]
    }),

    // Remove background from uploaded image
    removeBgFromUpload: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: API_ENDPOINTS.CLOSET.REMOVE_BG_UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response) => {
        if (response?.success && response?.file) {
          return {
            id: response.file.id,
            url: response.file.url
          };
        }
        throw new Error(response?.error || 'Background removal failed');
      }
    }),

    // Remove background from image URL
    removeBgFromUrl: builder.mutation({
      query: (imageUrl) => ({
        url: API_ENDPOINTS.CLOSET.REMOVE_BG_URL,
        method: 'POST',
        body: { imageUrl }
      }),
      transformResponse: (response) => {
        if (response?.success && response?.file) {
          return {
            id: response.file.id,
            url: response.file.url
          };
        }
        throw new Error(response?.error || 'Background removal failed');
      }
    }),

    // Get VTO count for clothing item
    getClothingVTOCount: builder.query({
      query: (id) => `/closet-clothes/${id}/vto-count`
    }),

    // Cascade delete clothing item and all associated VTO results
    cascadeDeleteClothing: builder.mutation({
      query: (id) => ({
        url: `/closet-clothes/${id}/cascade`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Clothing', 'VTOResult']
    }),

    // Toggle between original and removed background image
    toggleClothingBackground: builder.mutation({
      query: (id) => ({
        url: `/closet-clothes/${id}/toggle-background`,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            useRemovedImage: item.useRemovedImage
          };
        }
        throw new Error(response?.message || 'Toggle failed');
      }
    }),

    // Remove background from clothing item (saves to removedBgImage field)
    removeClothingBackground: builder.mutation({
      query: (id) => ({
        url: `/closet-clothes/${id}/remove-background`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            clothesName: item.clothesName,
            size: item.size,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            useRemovedImage: item.useRemovedImage
          };
        }
        throw new Error(response?.message || 'Background removal failed');
      }
    }),

    // Get VTO count for multiple clothing items
    getBulkVTOCount: builder.mutation({
      query: (ids) => ({
        url: '/closet-clothes/bulk-vto-count',
        method: 'POST',
        body: { ids }
      }),
      transformResponse: (response) => ({
        totalVTOCount: response.totalVTOCount || 0,
        clothingItemIds: response.clothingItemIds || []
      })
    }),

    // Bulk delete multiple clothing items with cascade
    bulkDeleteClothing: builder.mutation({
      query: (ids) => ({
        url: '/closet-clothes/bulk-delete',
        method: 'POST',
        body: { ids }
      }),
      invalidatesTags: ['ClosetClothes', 'VTOResult'],
      transformResponse: (response) => {
        if (response?.success) {
          return {
            deletedCount: response.deletedCount,
            vtoResultsDeleted: response.vtoResultsDeleted,
            deletedIds: response.deletedIds
          };
        }
        throw new Error(response?.message || 'Bulk delete failed');
      }
    }),

    // Check if clothing with the same image already exists
    checkExistingClothing: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append('image', file);

        return {
          url: '/closet-clothes/check-existing',
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        }
        return { success: false, exists: false, data: null };
      }
    }),

    // ===== CLOSET FOLDERS ENDPOINTS =====

    // Get user's closet folders
    getClosets: builder.query({
      query: () => '/closets/me',
      refetchOnFocus: true,
      providesTags: ['Closets'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          // Include closet_clothes for membership checking
          return response.data.map(closet => ({
            ...closet,
            closet_clothes: closet.closet_clothes || []
          }));
        }
        return [];
      }
    }),

    // Get clothing items for a specific closet
    getClosetClothes: builder.query({
      query: (closetId) => `/closets/${closetId}/clothes`,
      providesTags: (result, error, closetId) => [
        { type: 'ClosetClothes', id: `closet-${closetId}` },
        'ClosetClothes'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return {
            items: response.data.map(item => ({
              id: item.id,
              clothesName: item.clothesName,
              size: item.size,
              image: item.image ? {
                id: item.image.id,
                url: item.image.url,
                thumbnailUrl: item.image.formats?.thumbnail?.url || item.image.url
              } : null,
              removedBgImage: item.removedBgImage ? {
                id: item.removedBgImage.id,
                url: item.removedBgImage.url
              } : null,
              useRemovedImage: item.useRemovedImage || false,
              isPublic: item.isSharedForTryOn ?? item.isPublic ?? false,
              source_listing: item.source_listing,
              source_product: item.source_product,
              sourceVtoResult: item.sourceVtoResult?.id || item.sourceVtoResult || null,
              origin: item.origin || 'upload',
              licensingEnabled: item.source_listing?.licensingEnabled || item.source_product?.licensingEnabled || item.licensingEnabled || false,
              licensingType: item.source_listing?.licensingType || item.source_product?.licensingType || item.licensingType || null,
              licensingPrice: item.source_listing?.licensingPrice || item.source_product?.licensingPrice || item.licensingPrice || null,
              currency: item.source_listing?.currency || item.source_product?.currency || item.currency || 'USD',
              generatedProductPhoto: item.source_listing?.generatedProductPhoto ? {
                id: item.source_listing.generatedProductPhoto.id,
                url: item.source_listing.generatedProductPhoto.url
              } : item.source_product?.generatedProductPhoto ? {
                id: item.source_product.generatedProductPhoto.id,
                url: item.source_product.generatedProductPhoto.url
              } : null,
              addedAt: item.addedAt || item.created_at,
            createdAt: item.created_at,
              updatedAt: item.updated_at
            })),
            closetName: response.closetName,
            isDefault: response.isDefault
          };
        }
        return { items: [], closetName: '', isDefault: false };
      }
    }),

    // Create a new closet
    createCloset: builder.mutation({
      query: ({ name, isPublic }) => ({
        url: '/closets',
        method: 'POST',
        body: { name, isSharedForTryOn: isPublic }
      }),
      invalidatesTags: ['Closets'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        throw new Error(response?.message || 'Failed to create closet');
      }
    }),

    // Update closet name
    updateCloset: builder.mutation({
      query: ({ id, name, isPublic }) => ({
        url: `/closets/${id}`,
        method: 'PUT',
        body: { name, isSharedForTryOn: isPublic }
      }),
      invalidatesTags: ['Closets'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        throw new Error(response?.message || 'Failed to update closet');
      }
    }),

    // Delete a closet folder
    deleteCloset: builder.mutation({
      query: (id) => ({
        url: `/closets/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Closets']
    }),

    // Add clothing items to a closet
    addItemsToCloset: builder.mutation({
      query: ({ closetId, itemIds }) => ({
        url: `/closets/${closetId}/add-items`,
        method: 'POST',
        body: { itemIds }
      }),
      invalidatesTags: ['Closets', 'ClosetClothes']
    }),

    // Remove clothing items from a closet
    removeItemsFromCloset: builder.mutation({
      query: ({ closetId, itemIds }) => ({
        url: `/closets/${closetId}/remove-items`,
        method: 'POST',
        body: { itemIds }
      }),
      invalidatesTags: ['Closets', 'ClosetClothes']
    }),

    // Toggle isPublic on a clothing item (for SUSU Map sharing)
    togglePublishClothing: builder.mutation({
      query: ({ id, isPublic }) => ({
        url: API_ENDPOINTS.CLOSET.UPDATE(id),
        method: 'PUT',
        body: { isSharedForTryOn: isPublic }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ClosetClothes', id },
        'ClosetClothes',
        'SharedCloset'
      ]
    })
  })
});

// Export hooks for the closet API
export const {
  useClosetPageQuery,
  useGetUserClothingQuery,
  useUploadClothingMutation,
  useCreateClothingMutation,
  useCreateClothingFromUrlMutation,
  useAddFromListingMutation,
  useExtractClothingFromUrlMutation,
  useUpdateClothingMutation,
  useUpdateClothingWithUploadMutation,
  useDeleteClothingMutation,
  useRemoveBgFromUploadMutation,
  useRemoveBgFromUrlMutation,
  useGetClothingVTOCountQuery,
  useCascadeDeleteClothingMutation,
  useToggleClothingBackgroundMutation,
  useRemoveClothingBackgroundMutation,
  useGetBulkVTOCountMutation,
  useBulkDeleteClothingMutation,
  useCheckExistingClothingMutation,
  // Closet folders
  useGetClosetsQuery,
  useGetClosetClothesQuery,
  useCreateClosetMutation,
  useUpdateClosetMutation,
  useDeleteClosetMutation,
  useAddItemsToClosetMutation,
  useRemoveItemsFromClosetMutation,
  useTogglePublishClothingMutation
} = enhancedClosetApi;

export default enhancedClosetApi;